// Accessibility and display (APPLE-SPEC §5.8; shell-ia §9.1; DESIGN-SYSTEM §15): Text size, Appearance and Look as segmented controls, then
// navy switches for Increase contrast, Reduce transparency and Reduce motion. A switch that is off follows the device (the OS setting still
// applies). Every choice is a server-side preference, so it follows the person to other browsers (SH-10). Simplified 15 Oct 2026: the
// toolbar's ♿ and the status bar's link are gone; the account menu's "Accessibility and display…" opens these rows as a dialog (a sheet
// on a phone, `AccessibilityDialog`), and Settings › Accessibility shows them inline (`AccessibilityPanel`). WP-SHELL.
import { useId, type ReactNode } from "react";
import { Accessibility } from "lucide-react";
import { useTranslation } from "@ncp/i18n";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@ncp/ui";
import type { TextSize, ThemeChoice } from "../theme";
import { SegmentedControl, Switch } from "../theme/primitives";
import { TEXT_SIZES, useDisplaySettings } from "../prefs";
import { LookRow } from "./LookPicker";
import { FaceRow } from "./FacePicker";

const GLYPH: Record<TextSize, string> = { default: "A", large: "A+", larger: "A++" };

export function AccessibilityPanel() {
  const { t } = useTranslation();
  const { display, setDisplay, theme, setTheme } = useDisplaySettings();
  const themes: { value: ThemeChoice; label: string }[] = [
    { value: "light", label: t("assistant:econic.shell.theme.light") },
    { value: "dark", label: t("assistant:econic.shell.theme.dark") },
    { value: "system", label: t("assistant:econic.shell.theme.auto") },
  ];
  return (
    <div data-econic-a11y="">
      <div className="row3">
        <span className="lb">{t("assistant:econic.shell.a11y.textSize")}</span>
        <SegmentedControl<TextSize>
          label={t("assistant:econic.shell.a11y.textSize")}
          value={display.text}
          onChange={(text) => setDisplay({ text })}
          options={TEXT_SIZES.map((s) => ({ value: s, label: <span dir="ltr">{GLYPH[s]}</span>, ariaLabel: t(`assistant:econic.shell.a11y.${s === "default" ? "standard" : s}`) }))}
        />
      </div>
      <div className="row3">
        <span className="lb">{t("assistant:econic.shell.account.appearance")}</span>
        <SegmentedControl<ThemeChoice> label={t("assistant:econic.shell.account.appearance")} value={theme} onChange={setTheme} options={themes} />
      </div>
      <LookRow />
      <FaceRow />
      <div className="ms" role="separator" />
      <SwitchRow label={t("assistant:econic.shell.a11y.contrast")}>
        {(id) => <Switch aria-labelledby={id} checked={display.contrast === "more"} onChange={(on) => setDisplay({ contrast: on ? "more" : "normal" })} />}
      </SwitchRow>
      <SwitchRow label={t("assistant:econic.shell.a11y.transparency")}>
        {(id) => <Switch aria-labelledby={id} checked={display.transparency === "reduce"} onChange={(on) => setDisplay({ transparency: on ? "reduce" : "system" })} />}
      </SwitchRow>
      <SwitchRow label={t("assistant:econic.shell.a11y.motion")}>
        {(id) => <Switch aria-labelledby={id} checked={display.motion === "reduce"} onChange={(on) => setDisplay({ motion: on ? "reduce" : "system" })} />}
      </SwitchRow>
    </div>
  );
}

function SwitchRow({ label, children }: { label: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="row3">
      <span className="lb" id={id}>{label}</span>
      {children(id)}
    </div>
  );
}

/** The same rows as a dialog (a sheet on a phone), from the account menu where the toolbar's ♿ is not shown. */
export function AccessibilityDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" className="es-a11y-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Accessibility className="size-4 text-label-2" aria-hidden />{t("assistant:econic.shell.a11y.title")}</DialogTitle></DialogHeader>
        <div className="es-pop-rows"><AccessibilityPanel /></div>
      </DialogContent>
    </Dialog>
  );
}
